import { useEffect, useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { EmployerLayout } from "@/components/employer/portal/EmployerLayout";
import { apiFetch, formatApiError, formatDate, inputClass, Kicker, PrimaryButton, Spinner } from "@/components/employer/portal/shared";

export default function EmployerMessages() {
    const [items, setItems] = useState(null);
    const [form, setForm] = useState({ subject: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    const load = () => apiFetch("/employer/messages").then((r) => (r.ok ? r.json() : [])).then(setItems);
    useEffect(() => { load(); }, []);

    const send = async () => {
        if (busy) return;
        setBusy(true);
        setError("");
        setSent(false);
        const res = await apiFetch("/employer/messages", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(form),
        });
        if (res.ok) {
            setForm({ subject: "", message: "" });
            setSent(true);
            load();
        } else {
            const body = await res.json().catch(() => ({}));
            setError(formatApiError(body.detail, "Message could not be sent."));
        }
        setBusy(false);
    };

    return (
        <EmployerLayout title="Messages" subtitle="Contact the Afrinexa recruitment team from your secure employer workspace.">
            <div className="grid gap-8 lg:grid-cols-[0.45fr_0.55fr]" data-testid="employer-messages-page">
                <section className="h-fit border border-ink/10 bg-night p-7 text-ivory">
                    <Kicker dark>Message Afrinexa</Kicker>
                    <div className="mt-6 space-y-4">
                        <div><span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.16em] text-ivory/55">Subject</span><input data-testid="message-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={`${inputClass} border-ivory/15 bg-ivory/[0.04] text-ivory placeholder:text-ivory/35 focus:border-champagne`} /></div>
                        <div><span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.16em] text-ivory/55">Message</span><textarea data-testid="message-body" rows={7} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputClass} resize-y border-ivory/15 bg-ivory/[0.04] text-ivory placeholder:text-ivory/35 focus:border-champagne`} /></div>
                        {error && <p data-testid="message-error" className="border-l-2 border-champagne pl-4 text-sm text-ivory/70">{error}</p>}
                        <div className="flex items-center gap-3">
                            <PrimaryButton data-testid="message-send" busy={busy} onClick={send} className="bg-ivory text-ink hover:bg-champagne"><Send size={14} /> Send Message</PrimaryButton>
                            {sent && <span data-testid="message-sent" className="inline-flex items-center gap-1.5 text-sm text-champagne"><CheckCircle2 size={15} /> Sent</span>}
                        </div>
                    </div>
                </section>
                <section>
                    {!items ? <Spinner /> : items.length === 0 ? <p className="border-t border-ink/10 py-12 text-sm text-ink/50">No messages yet.</p> : (
                        <div className="border-t border-ink/10" data-testid="messages-list">
                            {items.map((item) => (
                                <article key={item.id} data-testid={`message-${item.id}`} className="border-b border-ink/10 py-5">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <h2 className="font-serif text-xl font-medium">{item.subject}</h2>
                                        <span className="text-xs text-ink/45">{formatDate(item.created_at, true)}</span>
                                    </div>
                                    <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink/65">{item.message}</p>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </EmployerLayout>
    );
}
